import { useState } from 'react';
import type { ReactNode } from 'react';
import { toDateString } from '@koob/dates';
import {
  GenderEnum,
  ChildAgeModeEnum,
  type FolderFormValues,
  type FolderTravellerDetailsSectionProps,
  type TravellerFieldsProps,
} from './types';
import {
  countIncompleteTravellers,
  isTravellerComplete,
} from './folderFormState';

export type { FolderTravellerDetailsSectionProps, TravellerFieldsProps };

export function FolderTravellerDetailsSection({
  values,
  setFieldValue,
  renderTextField,
  renderSelectField,
  renderDateField,
  renderNationalityField,
  t,
  requireCompleteTravellers = false,
}: FolderTravellerDetailsSectionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const totalTravellers = values.adults + values.children.length;
  const completedCount = totalTravellers - countIncompleteTravellers(values);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between text-sm text-gray-500">
        <span>
          {t(
            requireCompleteTravellers
              ? 'travellerDetailsRequiredDescription'
              : 'travellerDetailsDescription',
          )}
        </span>
        <span className="rounded bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
          {t('travellers.completedProgress', {
            done: completedCount,
            total: totalTravellers,
          })}
        </span>
      </div>

      <div className="space-y-3">
        {/* Adult cards */}
        {Array.from({ length: values.adults }, (_, i) => {
          const index = i;
          const detail = values.travelers[index] ?? {};
          const complete = isTravellerComplete(detail);
          const isOpen = openIndex === index;
          const nameSummary =
            detail.firstName || detail.lastName
              ? `${detail.firstName ?? ''} ${detail.lastName ?? ''}`.trim()
              : t('noDetailsYet');

          return (
            <div
              key={`adult-${i}`}
              className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
            >
              <div
                className="flex cursor-pointer items-center justify-between"
                onClick={() => setOpenIndex(isOpen ? null : index)}
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-amber-100 text-xs font-bold text-amber-800">
                    A{i + 1}
                  </span>
                  <span className="font-bold text-gray-900">
                    {t('travellers.adultTitle', { index: i + 1 })}
                  </span>
                  <span className="text-sm text-gray-400 font-normal ml-1">
                    {nameSummary}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      complete
                        ? 'bg-green-100 text-green-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}
                  >
                    {complete
                      ? t('travellers.complete')
                      : t('travellers.toComplete')}
                  </span>
                  <button
                    type="button"
                    className="text-xs font-semibold text-orange-600 hover:underline border-none bg-transparent p-0 cursor-pointer"
                  >
                    {isOpen
                      ? t('close')
                      : detail.firstName || detail.lastName
                        ? t('edit')
                        : t('addDetails')}
                  </button>
                </div>
              </div>

              {isOpen && (
                <div className="mt-4 border-t border-gray-100 pt-4">
                  <TravellerFields
                    index={index}
                    values={values}
                    detail={detail}
                    birthdate={toDateString(detail.birthdate) ?? ''}
                    setFieldValue={setFieldValue}
                    renderTextField={renderTextField}
                    renderSelectField={renderSelectField}
                    renderDateField={renderDateField}
                    renderNationalityField={renderNationalityField}
                    t={t}
                  />
                </div>
              )}
            </div>
          );
        })}

        {/* Child cards */}
        {Array.from({ length: values.children.length }, (_, j) => {
          const index = values.adults + j;
          const detail = values.travelers[index] ?? {};
          const childEntry = values.children[j];
          // Always a "YYYY-MM-DD" day: the apps' date fields store Date objects,
          // and one of those rendered below as a React child takes the whole
          // modal down (KD-3546).
          const childBirthdate =
            (toDateString(detail.birthdate) ?? '') ||
            (toDateString(childEntry?.birthdate) ?? '') ||
            '';
          const isApprox =
            detail.ageIsExact === false ||
            (detail.ageIsExact !== true && childEntry?.ageIsExact === false);
          const takenFromTravellers =
            !detail.birthdate && !!childEntry?.birthdate && !isApprox;
          // The card shows the date it fell back on: count it as known.
          const complete = isTravellerComplete({
            ...detail,
            birthdate: childBirthdate,
          });
          const isOpen = openIndex === index;
          const nameSummary =
            detail.firstName || detail.lastName
              ? `${detail.firstName ?? ''} ${detail.lastName ?? ''}`.trim()
              : t('noDetailsYet');

          return (
            <div
              key={`child-${j}`}
              className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
            >
              <div
                className="flex cursor-pointer items-center justify-between"
                onClick={() => setOpenIndex(isOpen ? null : index)}
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-800">
                    C{j + 1}
                  </span>
                  <span className="font-bold text-gray-900">
                    {t('travellers.childTitle', { index: j + 1 })}
                  </span>
                  <span className="text-sm text-gray-400 font-normal ml-1">
                    {nameSummary}
                  </span>
                  {childBirthdate && (
                    <span className="text-xs text-gray-400">
                      (
                      {isApprox
                        ? t('travellers.approxAge', {
                            age: childEntry?.age ?? detail.age ?? '',
                          })
                        : childBirthdate}
                      )
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      complete
                        ? 'bg-green-100 text-green-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}
                  >
                    {complete
                      ? t('travellers.complete')
                      : t('travellers.toComplete')}
                  </span>
                  <button
                    type="button"
                    className="text-xs font-semibold text-orange-600 hover:underline border-none bg-transparent p-0 cursor-pointer"
                  >
                    {isOpen
                      ? t('close')
                      : detail.firstName || detail.lastName
                        ? t('edit')
                        : t('addDetails')}
                  </button>
                </div>
              </div>

              {isOpen && (
                <div className="mt-4 border-t border-gray-100 pt-4">
                  <TravellerFields
                    index={index}
                    values={values}
                    detail={detail}
                    birthdate={childBirthdate}
                    birthdateNote={
                      takenFromTravellers
                        ? t('takenFromTravellersStep')
                        : isApprox
                          ? t('estimatedFromAge', {
                              age: childEntry?.age ?? detail.age,
                            })
                          : undefined
                    }
                    birthdateNoteTone={isApprox ? 'warning' : 'muted'}
                    setFieldValue={setFieldValue}
                    renderTextField={renderTextField}
                    renderSelectField={renderSelectField}
                    renderDateField={renderDateField}
                    renderNationalityField={renderNationalityField}
                    t={t}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TravellerFields({
  index,
  values,
  detail,
  birthdate,
  birthdateNote,
  birthdateNoteTone = 'muted',
  setFieldValue,
  renderTextField,
  renderSelectField,
  renderDateField,
  renderNationalityField,
  t,
}: TravellerFieldsProps & { values: FolderFormValues }) {
  const name = (field: string) => `travelers[${index}].${field}`;

  const handleBirthdateChange = (dateVal: string) => {
    setFieldValue(name('birthdate'), dateVal);
    setFieldValue(name('ageIsExact'), true);

    const isChild = index >= values.adults;
    if (isChild) {
      const childIndex = index - values.adults;
      const nextChildren = [...values.children];
      if (nextChildren[childIndex]) {
        nextChildren[childIndex] = {
          ...nextChildren[childIndex],
          mode: ChildAgeModeEnum.DATE,
          birthdate: dateVal || null,
          age: null,
          ageIsExact: true,
        };
        setFieldValue('children', nextChildren);
      }
    }
  };

  const labels = {
    gender: t('travellers.gender'),
    firstName: t('travellers.firstName'),
    lastName: t('travellers.lastName'),
    nationality: t('travellers.nationality'),
    birthdate: t('travellers.birthdate'),
    passportNumber: t('travellers.passportNumber'),
    passportExpirationDate: t('travellers.passportExpirationDate'),
  };
  const placeholders = {
    selectAnOption: t('selectAnOption', { defaultValue: 'Select an option' }),
    firstName: t('travellers.firstName', { defaultValue: 'First name' }),
    lastName: t('travellers.lastName', { defaultValue: 'Last name' }),
    passportNumber: t('travellers.passportNumber', {
      defaultValue: 'Passport number',
    }),
  };
  const genderOptions = [
    { label: t('travellers.male'), value: GenderEnum.MALE },
    { label: t('travellers.female'), value: GenderEnum.FEMALE },
    { label: t('travellers.other'), value: GenderEnum.OTHER },
  ];

  const Field = ({
    label,
    children,
    required,
  }: {
    label: string;
    children: ReactNode;
    required?: boolean;
  }) => (
    <div>
      <label className="mb-1 block text-xs font-semibold text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
    </div>
  );

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
      {/* Row 1 */}
      <div>
        {renderSelectField ? (
          renderSelectField(name('gender'), `${labels.gender} *`, genderOptions)
        ) : (
          <Field label={labels.gender} required>
            <select
              value={detail.gender ?? ''}
              onChange={(e) => setFieldValue(name('gender'), e.target.value)}
              className="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm focus:ring-2 focus:ring-orange-500"
            >
              <option value="">{placeholders.selectAnOption}</option>
              {genderOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>
        )}
      </div>

      <div>
        {renderTextField ? (
          renderTextField(name('firstName'), `${labels.firstName} *`)
        ) : (
          <Field label={labels.firstName} required>
            <input
              type="text"
              placeholder={placeholders.firstName}
              value={detail.firstName ?? ''}
              onChange={(e) => setFieldValue(name('firstName'), e.target.value)}
              className="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm focus:ring-2 focus:ring-orange-500"
            />
          </Field>
        )}
      </div>

      <div>
        {renderTextField ? (
          renderTextField(name('lastName'), `${labels.lastName} *`)
        ) : (
          <Field label={labels.lastName} required>
            <input
              type="text"
              placeholder={placeholders.lastName}
              value={detail.lastName ?? ''}
              onChange={(e) => setFieldValue(name('lastName'), e.target.value)}
              className="w-full rounded-lg border border-gray-300 p-2 text-sm focus:ring-2 focus:ring-orange-500"
            />
          </Field>
        )}
      </div>

      <div>
        {renderDateField ? (
          renderDateField(name('birthdate'), `${labels.birthdate} *`)
        ) : (
          <Field label={labels.birthdate} required>
            <input
              type="date"
              value={birthdate}
              onChange={(e) => handleBirthdateChange(e.target.value)}
              className="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm focus:ring-2 focus:ring-orange-500"
            />
          </Field>
        )}
        {birthdateNote && (
          <span
            className={`mt-1 block text-xs ${
              birthdateNoteTone === 'warning'
                ? 'text-amber-700'
                : 'text-gray-400'
            }`}
          >
            {birthdateNote}
          </span>
        )}
      </div>

      {/* Row 2 */}
      <div>
        {renderNationalityField ? (
          renderNationalityField(name('nationality'), labels.nationality)
        ) : (
          <Field label={labels.nationality}>
            <input
              type="text"
              placeholder={placeholders.selectAnOption}
              value={detail.nationality ?? ''}
              onChange={(e) =>
                setFieldValue(name('nationality'), e.target.value)
              }
              className="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm focus:ring-2 focus:ring-orange-500"
            />
          </Field>
        )}
      </div>

      <div className="sm:col-span-2">
        {renderTextField ? (
          renderTextField(name('passportNumber'), labels.passportNumber)
        ) : (
          <Field label={labels.passportNumber}>
            <input
              type="text"
              placeholder={placeholders.passportNumber}
              value={detail.passportNumber ?? ''}
              onChange={(e) =>
                setFieldValue(name('passportNumber'), e.target.value)
              }
              className="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm focus:ring-2 focus:ring-orange-500"
            />
          </Field>
        )}
      </div>

      <div>
        {renderDateField ? (
          renderDateField(
            name('passportExpirationDate'),
            labels.passportExpirationDate,
          )
        ) : (
          <Field label={labels.passportExpirationDate}>
            <input
              type="date"
              value={
                typeof detail.passportExpirationDate === 'string'
                  ? detail.passportExpirationDate
                  : (toDateString(detail.passportExpirationDate) ?? '')
              }
              onChange={(e) =>
                setFieldValue(name('passportExpirationDate'), e.target.value)
              }
              className="w-full h-10 rounded-lg border border-gray-300 px-3 text-sm focus:ring-2 focus:ring-orange-500"
            />
          </Field>
        )}
      </div>
    </div>
  );
}
